import { MarkdownParser } from '@repo/lib/markdown';
import { getPointsLabel } from '@repo/lib/quiz/getPointsLabel';
import { ArrowLeftRightIcon, ImagePlusIcon, TrashIcon, TypeIcon } from 'lucide-react';
import { useState } from 'react';
import { useWatch } from 'react-hook-form';

import { AnswerExplanationDialog, AnswerExplanationSection, AnswerMediaSelectDialog } from '@/components/features/quiz/question/question-builder';
import { Button, ButtonGroup } from '@/components/ui/button';
import { ErrorMessage, FormControl, FormField, FormItem } from '@/components/ui/form';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupTextarea } from '@/components/ui/input-group';
import { Markdown } from '@/components/ui/markdown';
import { NumberSelect } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import type { QuestionMatchingAnswerFieldsetProps } from '.';

export function QuestionMatchingAnswerFieldset(questionMatchingAnswerFieldsetProps: QuestionMatchingAnswerFieldsetProps) {
  const { form, index, removeAnswer, canRemove } = questionMatchingAnswerFieldsetProps;
  const { control } = form;

  const [openExplanation, setOpenExplanation] = useState<boolean>(false);
  const [openMediaDialog, setOpenMediaDialog] = useState<boolean>(false);
  const [mediaSide, setMediaSide] = useState<'left' | 'right' | null>(null);

  const removeExplanation = () => {
    form.setValue(`answers.${index}.explanation`, undefined);
  };

  const handleToggleType = (side: 'left' | 'right') => {
    if (side === 'left') {
      if (isLeftMediaType) {
        form.setValue(`answers.${index}.left.content`, '');
      } else {
        setMediaSide('left');
        setOpenMediaDialog(true);
      }
    } else {
      if (isRightMediaType) {
        form.setValue(`answers.${index}.right.content`, '');
      } else {
        setMediaSide('right');
        setOpenMediaDialog(true);
      }
    }
  };

  const handleMediaContentChange = (content: string) => {
    if (mediaSide === 'left') {
      form.setValue(`answers.${index}.left.content`, content);
    } else if (mediaSide === 'right') {
      form.setValue(`answers.${index}.right.content`, content);
    }
    setOpenMediaDialog(false);
    setMediaSide(null);
  };

  const explanation = useWatch({
    control: control,
    name: `answers.${index}.explanation`,
  });

  const leftAnswerValue: string = useWatch({
    control: control,
    name: `answers.${index}.left.content`,
  });
  const rightAnswerValue: string = useWatch({
    control: control,
    name: `answers.${index}.right.content`,
  });

  const isLeftMediaType: boolean = MarkdownParser.hasElementTypes(leftAnswerValue);
  const isRightMediaType: boolean = MarkdownParser.hasElementTypes(rightAnswerValue);

  return (
    <div className="bg-card flex w-full flex-col rounded-xl border shadow-sm">
      <AnswerMediaSelectDialog open={openMediaDialog} onOpenChange={setOpenMediaDialog} contentChange={handleMediaContentChange} />
      <div className="flex justify-end border-b p-2">
        <ButtonGroup>
          <FormField
            name={`answers.${index}.points`}
            render={({ field }) => {
              return (
                <FormItem>
                  <FormControl>
                    <NumberSelect
                      value={field.value == null ? undefined : String(field.value)}
                      onValueChange={(value) => field.onChange(value === '' ? null : Number(value))}
                      start={1}
                      end={20}
                      getLabel={getPointsLabel}
                      placeholder="np. 3"
                      size="sm"
                      className="h-9 rounded-e-none"
                    />
                  </FormControl>
                </FormItem>
              );
            }}
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="sm" disabled={!canRemove} type="button" onClick={() => removeAnswer(index)}>
                <TrashIcon />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Usuń odpowiedź</TooltipContent>
          </Tooltip>
        </ButtonGroup>
      </div>

      <div className="flex flex-row items-center gap-2 p-2 xl:flex-col">
        <FormField
          name={`answers.${index}.left.content`}
          render={({ field }) => (
            <FormItem className="relative">
              <FormControl>
                {isLeftMediaType ? (
                  <div className="bg-accent/25 relative flex h-40 max-h-40 justify-center rounded-md border">
                    <Markdown content={field.value} videoClassName="rounded-md h-full" imageClassName="rounded-md h-full" />
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="sm" type="button" className="absolute right-2 bottom-2 z-20 h-8 min-h-8 w-8 min-w-8" onClick={() => handleToggleType('left')}>
                          <TypeIcon />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Przełącz na tekst</TooltipContent>
                    </Tooltip>
                  </div>
                ) : (
                  <InputGroup>
                    <InputGroupTextarea {...field} placeholder="Wprowadź odpowiedź" />
                    <InputGroupAddon align="block-end">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <InputGroupButton variant="ghost" size="sm" type="button" className={'ml-auto h-8 min-h-8 w-8 min-w-8'} onClick={() => handleToggleType('left')}>
                            <ImagePlusIcon />
                          </InputGroupButton>
                        </TooltipTrigger>
                        <TooltipContent>Przełącz na media</TooltipContent>
                      </Tooltip>
                    </InputGroupAddon>
                  </InputGroup>
                )}
              </FormControl>
            </FormItem>
          )}
        />
        <div className="mx-auto flex items-center">
          <ArrowLeftRightIcon className="text-muted-foreground size-4 shrink-0 xl:rotate-90" />
        </div>
        <FormField
          name={`answers.${index}.right.content`}
          render={({ field }) => (
            <FormItem className="relative">
              <FormControl>
                {isRightMediaType ? (
                  <div className="bg-accent/25 relative flex h-40 max-h-40 justify-center rounded-md border">
                    <Markdown content={field.value} videoClassName="rounded-md h-full" imageClassName="rounded-md h-full" />
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="sm" type="button" className="absolute right-2 bottom-2 z-20 h-8 min-h-8 w-8 min-w-8" onClick={() => handleToggleType('right')}>
                          <TypeIcon />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Przełącz na tekst</TooltipContent>
                    </Tooltip>
                  </div>
                ) : (
                  <InputGroup>
                    <InputGroupTextarea {...field} placeholder="Wprowadź odpowiedź" />
                    <InputGroupAddon align="block-end">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <InputGroupButton variant="ghost" size="sm" type="button" className={'ml-auto h-8 min-h-8 w-8 min-w-8'} onClick={() => handleToggleType('right')}>
                            <ImagePlusIcon />
                          </InputGroupButton>
                        </TooltipTrigger>
                        <TooltipContent>Przełącz na media</TooltipContent>
                      </Tooltip>
                    </InputGroupAddon>
                  </InputGroup>
                )}
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="mt-auto flex flex-col gap-2 p-2">
        <AnswerExplanationSection explanation={explanation} onOpenExplanation={() => setOpenExplanation(true)} />
        <FormField
          name={`answers.${index}.explanation`}
          render={({ field }) => (
            <AnswerExplanationDialog
              open={openExplanation}
              onOpenChange={setOpenExplanation}
              onRemoveExplanation={removeExplanation}
              value={field.value || null}
              onValueChange={(value) => field.onChange(value)}
            />
          )}
        />
        <ErrorMessage name={`answers.${index}.content`} className="min-h-0 empty:hidden" />
      </div>
    </div>
  );
}
